import { useState, useEffect, useRef } from 'react';
import { metricsAPI } from '../utils/api';
import Loading from '../components/Loading';
import ErrorMessage from '../components/ErrorMessage';
import OverviewTab from '../components/dashboard/OverviewTab';
import RevenueTab from '../components/dashboard/RevenueTab';
import ChurnTab from '../components/dashboard/ChurnTab';
import SourcesTab from '../components/dashboard/SourcesTab';
import ActivityTab from '../components/dashboard/ActivityTab';
import { FiRefreshCw, FiChevronDown, FiChevronUp, FiClock } from 'react-icons/fi';

const SectionHeader = ({ title, subtitle }) => (
  <div className="pt-4 pb-1">
    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
    {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
  </div>
);

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState(null);
  const [refreshLogs, setRefreshLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(true);
  const logsEndRef = useRef(null);

  useEffect(() => {
    if (refreshLogs.length > 0) {
      logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [refreshLogs]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await metricsAPI.getDashboardV2();
      setData(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setRefreshMessage(null);
    setError(null);
    setRefreshLogs([]);
    setShowLogs(true);

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token missing. Please log in again.');
      setRefreshing(false);
      return;
    }

    const eventSource = new EventSource(`/api/metrics/refresh-stream?token=${token}`);

    eventSource.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);

        if (parsed.type === 'log') {
          setRefreshLogs((prev) => [...prev, {
            message: parsed.message,
            type: parsed.logType,
            timestamp: new Date().toLocaleTimeString()
          }]);
        } else if (parsed.type === 'result') {
          if (parsed.success) {
            setRefreshMessage(
              `Synced ${parsed.synced?.customers || 0} customers, ${parsed.synced?.subscriptions || 0} subscriptions, ${parsed.synced?.checkoutSessions || 0} sessions, ${parsed.synced?.invoices || 0} invoices, ${parsed.synced?.refunds || 0} refunds in ${parsed.duration}`
            );
            // Re-fetch the comprehensive dashboard data
            fetchData();
          } else {
            setError(parsed.error || 'Refresh failed');
          }
          setRefreshing(false);
          eventSource.close();

          if (parsed.success) {
            setTimeout(() => setShowLogs(false), 10000);
          }
        }
      } catch (err) {
        console.error('Error parsing SSE data:', err);
      }
    };

    eventSource.onerror = () => {
      setError('Connection lost during refresh. Please try again.');
      setRefreshing(false);
      eventSource.close();
    };
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) return <Loading message="Loading dashboard..." />;
  if (error && !data) return <ErrorMessage message={error} onRetry={fetchData} />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Comprehensive subscription analytics
          </p>
        </div>
        <div className="flex items-center gap-3">
          {data?.generatedAt && (
            <span className="flex items-center gap-1.5 text-xs text-gray-400">
              <FiClock className="w-3 h-3" />
              {new Date(data.generatedAt).toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            <FiRefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh from Stripe'}
          </button>
        </div>
      </div>

      {/* Refresh Logs */}
      {(refreshing || refreshLogs.length > 0) && (
        <div className="bg-gray-900 rounded-xl overflow-hidden">
          <div
            className="flex items-center justify-between px-4 py-2 bg-gray-800 cursor-pointer"
            onClick={() => setShowLogs(!showLogs)}
          >
            <div className="flex items-center gap-2">
              {refreshing && <FiRefreshCw className="animate-spin text-blue-400 w-3.5 h-3.5" />}
              <span className="text-sm font-medium text-gray-300">
                {refreshing ? 'Syncing from Stripe...' : 'Sync Complete'}
              </span>
            </div>
            <button className="text-gray-400 hover:text-white">
              {showLogs ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
            </button>
          </div>
          {showLogs && (
            <div className="p-4 font-mono text-xs max-h-40 overflow-y-auto">
              {refreshLogs.map((log, index) => (
                <div
                  key={index}
                  className={`py-0.5 ${
                    log.type === 'error' ? 'text-red-400' :
                    log.type === 'success' ? 'text-green-400' :
                    'text-gray-400'
                  }`}
                >
                  <span className="text-gray-600">[{log.timestamp}]</span> {log.message}
                </div>
              ))}
              <div ref={logsEndRef} />
            </div>
          )}
        </div>
      )}

      {/* Success Message */}
      {refreshMessage && !refreshing && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl text-sm">
          {refreshMessage}
        </div>
      )}

      {/* Error (non-blocking) */}
      {error && data && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      {/* Overview */}
      <OverviewTab data={data} />

      {/* Revenue */}
      <div className="border-t border-gray-200" />
      <SectionHeader title="Revenue" subtitle="Deep dive into revenue, invoices, and top customers" />
      <RevenueTab data={data} />

      {/* Churn & Retention */}
      <div className="border-t border-gray-200" />
      <SectionHeader title="Churn & Retention" subtitle="Cancellations, at-risk subscriptions, and subscriber tenure" />
      <ChurnTab data={data} />

      {/* Sources & Sales */}
      <div className="border-t border-gray-200" />
      <SectionHeader title="Sources & Sales" subtitle="Attribution breakdown and salesperson performance" />
      <SourcesTab data={data} />

      {/* Activity & Invoices */}
      <div className="border-t border-gray-200" />
      <SectionHeader title="Activity & Invoices" subtitle="Recent events, refund analysis, and invoice volume" />
      <ActivityTab data={data} />
    </div>
  );
};

export default Dashboard;
